import { AudioUnitBox, TrackBox } from "@opendaw/studio-boxes";
import { IndexedBox, PrimitiveField } from "@opendaw/lib-box";
import { TrackType } from "@opendaw/studio-adapters";
import { isDefined, isNull, panic } from "@opendaw/lib-std";
import { UUID } from "@opendaw/lib-std";
import { Facade, Parameters } from "../Common";
import { Facades } from "../Facades";
import { AudioRegionImpl, NoteRegionImpl, Regions, ValueRegionImpl } from "./Regions";
import { AudioClipImpl, Clips, NoteClipImpl, ValueClipImpl } from "./Clips";
import { ModulatorImpls } from "../Modulators";
export class TrackFacade extends Facade {
    constructor(context, box) {
        super(context, box);
        this.bind({ enabled: box.enabled, excludePianoMode: box.excludePianoMode });
    }
    get index() { return this.box.index.getValue(); }
    get ownerBox() { return this.box.tracks.targetVertex.unwrap("track has no owner").box; }
    get owner() {
        const ownerBox = this.ownerBox;
        if (ownerBox instanceof AudioUnitBox) {
            return Facades.audioUnitOf(this.context, ownerBox);
        }
        return ModulatorImpls.wrap(this.context, ownerBox);
    }
    get audioUnit() {
        const ownerBox = this.ownerBox;
        return ownerBox instanceof AudioUnitBox ? Facades.audioUnitOf(this.context, ownerBox) : null;
    }
    remove() {
        this.context.edit(() => {
            const field = this.box.tracks.targetVertex.unwrap("track has no owner");
            const index = this.index;
            IndexedBox.removeOrder(field, index);
            this.box.delete();
        });
    }
}
export class NoteTrackImpl extends TrackFacade {
    type = "notes";
    constructor(context, box) { super(context, box); }
    get regions() {
        return Regions.list(this.context, this.box).filter((region) => region instanceof NoteRegionImpl);
    }
    get clips() {
        return Clips.list(this.context, this.box).filter((clip) => clip instanceof NoteClipImpl);
    }
    addRegion(props) { return Regions.createNoteRegion(this.context, this.box, props); }
    addClip(props) { return Clips.createNoteClip(this.context, this.box, props); }
}
export class AudioTrackImpl extends TrackFacade {
    type = "audio";
    constructor(context, box) { super(context, box); }
    get regions() {
        return Regions.list(this.context, this.box).filter((region) => region instanceof AudioRegionImpl);
    }
    get clips() {
        return Clips.list(this.context, this.box).filter((clip) => clip instanceof AudioClipImpl);
    }
    addRegion(sample, props) {
        return Regions.createAudioRegion(this.context, this.box, sample, props);
    }
    addClip(sample, props) {
        return Clips.createAudioClip(this.context, this.box, sample, props);
    }
}
export class ValueTrackImpl extends TrackFacade {
    type = "value";
    constructor(context, box) { super(context, box); }
    get targetField() {
        const vertex = this.box.target.targetVertex.unwrap("automation track has no target");
        if (!(vertex instanceof PrimitiveField)) {
            return panic("automation target is not a parameter");
        }
        return vertex;
    }
    get target() {
        const facade = Facades.forBox(this.context, this.targetField.box);
        if (isNull(facade)) {
            return panic(`No object found for automation target ${this.targetField.toString()}`);
        }
        return facade;
    }
    get parameter() {
        return Parameters.pathOf(this.target, this.targetField) ?? panic(`Unknown parameter path for ${this.targetField.toString()}`);
    }
    get regions() {
        return Regions.list(this.context, this.box).filter((region) => region instanceof ValueRegionImpl);
    }
    get clips() {
        return Clips.list(this.context, this.box).filter((clip) => clip instanceof ValueClipImpl);
    }
    addRegion(props) { return Regions.createValueRegion(this.context, this.box, props); }
    addClip(props) { return Clips.createValueClip(this.context, this.box, props); }
}
export var TrackImpls;
(function (TrackImpls) {
    TrackImpls.wrap = (context, box) => context.facade(box, () => {
        switch (box.type.getValue()) {
            case TrackType.Notes: return new NoteTrackImpl(context, box);
            case TrackType.Audio: return new AudioTrackImpl(context, box);
            case TrackType.Value: return new ValueTrackImpl(context, box);
            default: return panic(`Unsupported track type ${box.type.getValue()}`);
        }
    });
    TrackImpls.list = (context, field) => IndexedBox.collectIndexedBoxes(field, TrackBox)
        .filter(box => box.type.getValue() !== TrackType.Undefined)
        .map(box => TrackImpls.wrap(context, box));
    TrackImpls.reindex = (field) => IndexedBox.collectIndexedBoxes(field, TrackBox).forEach((box, index) => box.index.setValue(index));
    TrackImpls.create = (context, field, type, target, insertIndex) => context.edit(() => {
        const index = IndexedBox.insertOrder(field, isDefined(insertIndex) ? Math.round(insertIndex) : Number.MAX_SAFE_INTEGER);
        const box = TrackBox.create(context.boxGraph, UUID.generate(), box => {
            box.index.setValue(index);
            box.type.setValue(type);
            box.tracks.refer(field);
            box.target.refer(target);
        });
        return TrackImpls.wrap(context, box);
    });
})(TrackImpls || (TrackImpls = {}));
